<template>
  <div class="permission-tree-node" :class="{ 'has-children': node.children && node.children.length > 0 }">
    <!-- Nó pai ou folha -->
    <div 
      class="tree-node-item" 
      :class="{ 
        'is-group': node.children && node.children.length > 0, 
        'is-leaf': !node.children || node.children.length === 0,
        'level-0': level === 0,
        [`level-${level}`]: level > 0
      }"
    >
      <!-- Checkbox controlado por :checked para refletir sempre o estado do perfil (Marcar Todas / carregamento) -->
      <div class="tree-node-content">
        <label class="tree-checkbox-label">
          <input
            type="checkbox"
            :id="`permission_${roleId}_${node.id || node.name}`"
            :checked="isChecked"
            :indeterminate.prop="isIndeterminate"
            class="tree-checkbox-input"
            @change="handleChange($event)"
          />
          <span class="tree-node-label">
            <strong v-if="node.description">{{ node.description }}</strong>
            <small v-if="node.name" class="text-muted d-block">{{ node.id != null ? node.id + ' ' : '' }}{{ node.name }}</small>
          </span>
        </label>
      </div>
    </div>

    <!-- Filhos recursivos -->
    <div v-if="node.children && node.children.length > 0" class="tree-children">
      <PermissionTreeNode
        v-for="child in node.children"
        :key="child.id || child.name"
        :node="child"
        :level="level + 1"
        :roleId="roleId"
        :selectedPermissions="selectedPermissions"
        @permission-change="handlePermissionChange"
        @permissions-batch-change="handleBatchChange"
      />
    </div>
  </div>
</template>

<script>
export default {
  name: 'PermissionTreeNode',
  props: {
    node: {
      type: Object,
      required: true
    },
    level: {
      type: Number,
      default: 0
    },
    roleId: {
      type: [Number, String],
      required: true
    },
    selectedPermissions: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    normalizedSelectedIds() {
      const arr = this.selectedPermissions || [];
      return arr.map(id => Number(id)).filter(id => !Number.isNaN(id));
    },
    isChecked() {
      const ids = this.normalizedSelectedIds;

      if (this.node.id !== undefined && this.node.id !== null) {
        const nodeId = Number(this.node.id);
        return ids.some(id => id === nodeId);
      }

      if (this.node.children && Array.isArray(this.node.children) && this.node.children.length > 0) {
        const childIds = this.collectAllPermissionIds(this.node.children);
        if (childIds.length === 0) return false;
        return childIds.every(nodeId => ids.some(id => id === nodeId));
      }

      return false;
    },
    isIndeterminate() {
      if (!this.node.children || this.node.children.length === 0) return false;
      const ids = this.normalizedSelectedIds;
      const childIds = this.collectAllPermissionIds(this.node.children);
      if (childIds.length === 0) return false;
      const selectedCount = childIds.filter(nodeId => ids.some(id => id === nodeId)).length;
      return selectedCount > 0 && selectedCount < childIds.length;
    }
  },
  methods: {
    collectAllPermissionIds(children) {
      if (!children || !Array.isArray(children)) return [];
      const ids = [];
      children.forEach(child => {
        if (child.id !== undefined && child.id !== null && !ids.includes(Number(child.id))) {
          ids.push(Number(child.id));
        }
        if (child.children && child.children.length > 0) {
          ids.push(...this.collectAllPermissionIds(child.children));
        }
      });
      return ids;
    },
    handleChange(ev) {
      const checked = ev && ev.target ? ev.target.checked : false;
      const isPermission = this.node.id !== undefined && this.node.id !== null;
      const isGroup = this.node.children && this.node.children.length > 0;

      if (isPermission) {
        this.handlePermissionChange(this.node.id, checked);
      } else if (isGroup) {
        const shouldSelectAll = this.isIndeterminate ? true : checked;
        this.selectAllChildren(this.node.children, shouldSelectAll);
      }
    },
    selectAllChildren(children, checked) {
      const permissionIds = this.collectAllPermissionIds(children);
      if (permissionIds.length > 0) {
        this.$emit('permissions-batch-change', permissionIds, checked);
      }
    },
    handlePermissionChange(permissionId, checked) {
      this.$emit('permission-change', permissionId, checked);
    },
    handleBatchChange(permissionIds, checked) {
      this.$emit('permissions-batch-change', permissionIds, checked);
    }
  }
};
</script>

<style scoped>
.permission-tree-node {
  position: relative;
}

.tree-node-item {
  position: relative;
  padding: 8px 0;
  padding-left: 0;
  transition: background-color 0.2s ease;
  margin-left: 0;
}

.tree-node-item.level-0 {
  border-left: 2px solid #007bff;
  padding-left: 15px;
  margin-bottom: 8px;
  font-weight: 600;
}

.tree-node-item.level-0.is-leaf {
  border-left: none;
  padding-left: 0;
  font-weight: normal;
}

.tree-node-item:hover {
  background-color: #f8f9fa;
  border-radius: 4px;
}

.tree-node-item.is-group {
  font-weight: 500;
}

.tree-node-item.is-leaf {
  font-weight: normal;
  padding-left: 20px;
  position: relative;
}

.tree-node-item.is-leaf::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  width: 15px;
  height: 1px;
  border-top: 1px dotted #dee2e6;
}

.tree-children {
  margin-left: 20px;
  border-left: 1px dotted #dee2e6;
  padding-left: 15px;
  position: relative;
}

.tree-node-content {
  display: flex;
  align-items: flex-start;
}

.tree-checkbox-label {
  display: flex;
  align-items: flex-start;
  cursor: pointer;
  margin: 0;
  padding-left: 0;
  width: 100%;
  user-select: none;
}

.tree-checkbox-input {
  margin: 0;
  margin-right: 10px;
  margin-top: 3px;
  flex-shrink: 0;
  cursor: pointer;
  width: 1.1em;
  height: 1.1em;
}

.tree-node-label {
  display: block;
  line-height: 1.4;
}

.tree-node-label strong {
  display: block;
  color: #212529;
}

.tree-node-label small {
  font-size: 0.85em;
  margin-top: 2px;
  display: block;
}
</style>
